import { Button, DialogContent, Input, Option, Select, selectClasses, Skeleton } from "@mui/joy";
import React, { useEffect, useState } from "react";
import { BASE_URL, BLACK, PRIMARY, WHITE } from "../../appConstants";
import Drawer from '@mui/joy/Drawer';
import axios from "axios";
import appError from "../../appError";
import toast from "react-hot-toast";
import PaginationComponent from "../commonComponents/PaginationComponent";
import { debounce } from "lodash";
import { useParams } from "react-router-dom";

type ProductObject = {
    product_id: string,
    brand_id: string,
    platform: string,
    product: string,
    product_type: string,
    created_time_stamp: string,
    updated_time_stamp: string,
    bcg_type: string,
    input_source: string,
    to_transfer: boolean,
    to_transfer_reason: string | null
}

type ProductUpdateObject = {
    product_id: string,
    bcg_type: string
}

const ProductMaster: React.FC = () => {
    const {brand_id: brandId, platform_id: platformId } = useParams();
    const [state, setState] = useState<ProductObject>({
        product_id: "",
        brand_id: "",
        platform: "",
        product: "",
        product_type: "",
        created_time_stamp: "",
        updated_time_stamp: "",
        bcg_type: "",
        input_source: "",
        to_transfer: false,
        to_transfer_reason: null
    });

    const [editDrawer, setEditDrawer] = useState(false);
    const [productData, setProductData] = useState<ProductObject[]>([]);
    const [loadingOnSave, setLoadingOnSave] = useState<boolean>(false);
    const [loadingProductData, setLoadingProductData] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [limit, setLimit] = useState<number>(10);
    const [loading, setLoading] = useState<string>('');
    // Debounced function to handle search clearing
    const debouncedSearchClear = debounce((value: string) => {
        if (!value.trim()) {
            setCurrentPage(1);
            fetchProductData();
        }
    }, 300);

    useEffect(() => {
       // fetchProductData();
    }, [brandId, platformId, currentPage, limit]);

    useEffect(() => {
       // debouncedSearchClear(searchTerm);
        return () => {
          //  debouncedSearchClear.cancel();
        };
    }, [searchTerm]);

    const fetchProductData = async () => {
        try {
            setLoadingProductData(true);
            const token = localStorage.getItem("token");
            if (!brandId && !platformId) {
                return;
            }
            
            let api = `${BASE_URL}brand/product?brand_id=${brandId}&platform_id=${platformId}&sub_module_for_platform_id=248e0c6a-89fc-4f24-bbcf-9060e38ff069&page=${currentPage}&limit=${limit}&type=mapped`;
            
            // Add search parameter if search term exists (trimmed from both sides)
            const trimmedSearchTerm = searchTerm.trim();
            if (trimmedSearchTerm) {
                api += `&search=${encodeURIComponent(trimmedSearchTerm)}`;
            }
            
            const productRes = await axios.get(api, {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            const productArray: ProductObject[] = productRes?.data?.data?.data;
            const total: number = productRes?.data?.data?.total;
            setProductData(productArray);
            setTotalItems(total);
        } catch (error) {
            appError(error);
        } finally {
            setLoadingProductData(false);
        }
    }

    const toggleDrawer = (open: boolean, product: ProductObject | null) => {
        if (product) {
            setState({ ...product });
        }
        setEditDrawer(open);
    };

    const onSaveClickHandler = async () => {
        try {
            setLoadingOnSave(true);
            const token = localStorage.getItem("token");
            
            const updateData: ProductUpdateObject = {
                product_id: state.product_id,
                bcg_type: state.bcg_type
            };

            const res = await axios.put(`${BASE_URL}brand/product?brand_id=${brandId}&platform_id=${platformId}&sub_module_for_platform_id=248e0c6a-89fc-4f24-bbcf-9060e38ff069`, updateData, {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            if (res?.data?.status === "success") {
                toast.success("Product updated successfully");
                
                // Update the local state without refreshing
                setProductData(prevState => {
                    return prevState.map(item => {
                        if (item.product_id === state.product_id) {
                            return { ...item, bcg_type: state.bcg_type };
                        }
                        return item;
                    });
                });

                setState({
                    product_id: "",
                    brand_id: "",
                    platform: "",
                    product: "",
                    product_type: "",
                    created_time_stamp: "",
                    updated_time_stamp: "",
                    bcg_type: "",
                    input_source: "",
                    to_transfer: false,
                    to_transfer_reason: null
                });
                setEditDrawer(false);
            }
        } catch (error) {
            appError(error);
        } finally {
            setLoadingOnSave(false);
        }
    }

    const onSelectBcgTypeHandler = (event: any, value: any) => {
        setState(prevState => ({ ...prevState, bcg_type: value }));
    }

    const handleSearch = () => {
        setCurrentPage(1);
        fetchProductData();
    }

    const downloadClickHandler = async (type: string) => {
        try {
            setLoading(type);
            const token = localStorage.getItem("token");
            const res = await axios.post(`${BASE_URL}brand/queueMaster?brand_id=${brandId}&platform_id=${platformId}&type=${type}`, {},
                {
                    headers: {
                        "Authorization": `Bearer ${token}`
                    }
                }
            );
            const { message } = res?.data?.data;
            toast.success(message, { duration: 5000 });

        } catch (error) {
            appError(error);
        } finally {
            setLoading("");
        }
    }

    return (
        <div className="flex flex-col pt-10 h-full" >
            <div className="h-full" >
                <div className="flex justify-between mx-10">
                    <div className="flex" style={{ marginBottom: 24 }} >
                        <Input
                            value={searchTerm}
                            onChange={(e) => {
                                const value = e.target.value;
                                setSearchTerm(value);
                            }}
                            startDecorator={
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M17.9422 17.0578L14.0305 13.1469C15.1642 11.7857 15.7296 10.0398 15.6089 8.27244C15.4883 6.50506 14.6909 4.85223 13.3826 3.65779C12.0744 2.46334 10.356 1.81926 8.58493 1.85951C6.81388 1.89976 5.12653 2.62125 3.87389 3.87389C2.62125 5.12653 1.89976 6.81388 1.85951 8.58492C1.81926 10.356 2.46335 12.0744 3.65779 13.3826C4.85223 14.6909 6.50506 15.4883 8.27245 15.6089C10.0398 15.7296 11.7857 15.1642 13.1469 14.0305L17.0578 17.9422C17.1159 18.0003 17.1848 18.0463 17.2607 18.0777C17.3366 18.1092 17.4179 18.1253 17.5 18.1253C17.5821 18.1253 17.6634 18.1092 17.7393 18.0777C17.8152 18.0463 17.8841 18.0003 17.9422 17.9422C18.0003 17.8841 18.0463 17.8152 18.0777 17.7393C18.1092 17.6634 18.1253 17.5821 18.1253 17.5C18.1253 17.4179 18.1092 17.3366 18.0777 17.2607C18.0463 17.1848 18.0003 17.1159 17.9422 17.0578ZM3.125 8.75C3.125 7.63748 3.4549 6.54994 4.07298 5.62491C4.69107 4.69989 5.56957 3.97892 6.5974 3.55317C7.62524 3.12743 8.75624 3.01604 9.84738 3.23308C10.9385 3.45012 11.9408 3.98585 12.7275 4.77252C13.5141 5.55919 14.0499 6.56147 14.2669 7.65261C14.484 8.74376 14.3726 9.87475 13.9468 10.9026C13.5211 11.9304 12.8001 12.8089 11.8751 13.427C10.9501 14.0451 9.86252 14.375 8.75 14.375C7.25866 14.3733 5.82888 13.7802 4.77435 12.7256C3.71982 11.6711 3.12665 10.2413 3.125 8.75Z" fill="#1C1C1C" fill-opacity="0.4" />
                                </svg>
                            }
                            sx={{
                                "--Input-focusedThickness": "0px",
                                ':focus-within': {
                                    border: `1px solid ${PRIMARY.P_BLue}`,
                                },
                                boxShadow: 'none',
                                height: 40,
                                width: 320,
                                borderRadius: 8,
                            }}
                            placeholder="Search"
                        />
                        <Button
                            onClick={handleSearch}
                            sx={{
                                height: 40,
                                width: 120,
                                fontSize: 14,
                                fontWeight: 500,
                                backgroundColor: PRIMARY.P_BLue,
                                lineHeight: "20px",
                                ":hover": {
                                    backgroundColor: PRIMARY.P_BLue
                                },
                                marginLeft: "12px",
                                borderRadius: 8
                            }}
                        >Search</Button>
                    </div>
                    <div className="">
                        <Button
                            loading={loading === "MASTER_PRODUCT"}
                            onClick={() => downloadClickHandler("MASTER_PRODUCT")}

                            endDecorator={
                                <svg xmlns="http://www.w3.org/2000/svg" width="21" height="20" viewBox="0 0 21 20" fill="none">
                                    <path d="M17.8172 10.4425L12.1922 16.0675C12.0749 16.1848 11.9159 16.2507 11.75 16.2507C11.5841 16.2507 11.4251 16.1848 11.3078 16.0675C11.1905 15.9503 11.1247 15.7912 11.1247 15.6253C11.1247 15.4595 11.1905 15.3004 11.3078 15.1832L15.8664 10.6253H3.625C3.45924 10.6253 3.30027 10.5595 3.18306 10.4423C3.06585 10.3251 3 10.1661 3 10.0003C3 9.83459 3.06585 9.67562 3.18306 9.55841C3.30027 9.4412 3.45924 9.37535 3.625 9.37535H15.8664L11.3078 4.81753C11.1905 4.70026 11.1247 4.5412 11.1247 4.37535C11.1247 4.2095 11.1905 4.05044 11.3078 3.93316C11.4251 3.81588 11.5841 3.75 11.75 3.75C11.9159 3.75 12.0749 3.81588 12.1922 3.93316L17.8172 9.55816C17.8753 9.61621 17.9214 9.68514 17.9529 9.76101C17.9843 9.83688 18.0005 9.91821 18.0005 10.0003C18.0005 10.0825 17.9843 10.1638 17.9529 10.2397C17.9214 10.3156 17.8753 10.3845 17.8172 10.4425Z" fill="white" />
                                </svg>
                            }
                            sx={{
                                height: "40px",
                                width: "200px",
                                backgroundColor: PRIMARY.P_BLue,
                            }}
                        >Download Master
                        </Button>
                    </div>
                </div>
                
               =
            </div>
            
            <div className="flex flex-row justify-end items-center px-10 py-4" style={{ height: 72, boxShadow: "0px -4px 40px 4px rgba(0, 0, 0, 0.05)" }}>
                <Button
                    sx={{
                        marginRight: "16px",
                        height: 40,
                        width: 220,
                        fontSize: 14,
                        fontWeight: 500,
                        backgroundColor: BLACK.B_5,
                        color: BLACK.B_100,
                        lineHeight: "20px",
                        ":hover": {
                            backgroundColor: BLACK.B_5
                        },
                        borderRadius: 8
                    }}
                    onClick={() => {
                        setCurrentPage(1);
                        setSearchTerm("");
                        fetchProductData();
                    }}
                >Refresh</Button>
            </div>
            
            {/* Edit Drawer */}
            <Drawer
                key={'right'}
                anchor={"right"}
                open={editDrawer}
                onClose={() => toggleDrawer(false, null)}
                size="sm"
                sx={{
                }}
            >
                <div className="flex flex-row justify-between p-6" style={{ height: 72, borderBottom: `1px solid ${BLACK.B_10}` }} >
                    <div style={{ fontSize: 18, fontWeight: 500, lineHeight: "24px", color: BLACK.B_100 }} >Product Details</div>
                    <div className="w-fit cursor-pointer" onClick={() => toggleDrawer(false, null)} >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M19.2806 18.2194C19.3502 18.289 19.4055 18.3718 19.4432 18.4628C19.4809 18.5539 19.5003 18.6514 19.5003 18.75C19.5003 18.8485 19.4809 18.9461 19.4432 19.0372C19.4055 19.1282 19.3502 19.2109 19.2806 19.2806C19.2109 19.3503 19.1281 19.4056 19.0371 19.4433C18.9461 19.481 18.8485 19.5004 18.7499 19.5004C18.6514 19.5004 18.5538 19.481 18.4628 19.4433C18.3717 19.4056 18.289 19.3503 18.2193 19.2806L11.9999 13.0603L5.78055 19.2806C5.63982 19.4213 5.44895 19.5004 5.24993 19.5004C5.05091 19.5004 4.86003 19.4213 4.7193 19.2806C4.57857 19.1399 4.49951 18.949 4.49951 18.75C4.49951 18.551 4.57857 18.3601 4.7193 18.2194L10.9396 12L4.7193 5.78061C4.57857 5.63988 4.49951 5.44901 4.49951 5.24999C4.49951 5.05097 4.57857 4.8601 4.7193 4.71936C4.86003 4.57863 5.05091 4.49957 5.24993 4.49957C5.44895 4.49957 5.63982 4.57863 5.78055 4.71936L11.9999 10.9397L18.2193 4.71936C18.36 4.57863 18.5509 4.49957 18.7499 4.49957C18.949 4.49957 19.1398 4.57863 19.2806 4.71936C19.4213 4.8601 19.5003 5.05097 19.5003 5.24999C19.5003 5.44901 19.4213 5.63988 19.2806 5.78061L13.0602 12L19.2806 18.2194Z" fill="#1C1C1C" />
                        </svg>
                    </div>
                </div>
                <DialogContent sx={{ paddingTop: "40px", paddingBottom: "16px", paddingX: "24px" }} >
                    <div className="mb-6" style={{ height: 72 }} >
                        <div className="mb-1" style={{ fontSize: 14, fontWeight: 500, lineHeight: "20px", color: BLACK.B_40 }} >Platform</div>
                        <Input
                            value={state.platform}
                            sx={{
                                "--Input-focusedThickness": "0px",
                                ':focus-within': {
                                    border: `1px solid ${PRIMARY.P_BLue}`,
                                },
                                boxShadow: 'none',
                                height: 52,
                                fontSize: 14,
                                fontWeight: 400,
                                lineHeight: "20px",
                                borderRadius: 8,
                                backgroundColor: BLACK.B_10
                            }}
                            placeholder="Platform"
                            disabled={true}
                        />
                    </div>
                    <div className="mb-6" style={{ height: 72 }} >
                        <div className="mb-1" style={{ fontSize: 14, fontWeight: 500, lineHeight: "20px", color: BLACK.B_40 }} >Product</div>
                        <Input
                            value={state.product}
                            sx={{
                                "--Input-focusedThickness": "0px",
                                ':focus-within': {
                                    border: `1px solid ${PRIMARY.P_BLue}`,
                                },
                                boxShadow: 'none',
                                height: 52,
                                fontSize: 14,
                                fontWeight: 400,
                                lineHeight: "20px",
                                borderRadius: 8,
                                backgroundColor: BLACK.B_10
                            }}
                            placeholder="Product"
                            disabled={true}
                        />
                    </div>
                    <div className="mb-6" style={{ height: 72 }} >
                        <div className="mb-1" style={{ fontSize: 14, fontWeight: 500, lineHeight: "20px", color: BLACK.B_40 }} >Product Type</div>
                        <Input
                            value={state.product_type}
                            sx={{
                                "--Input-focusedThickness": "0px",
                                ':focus-within': {
                                    border: `1px solid ${PRIMARY.P_BLue}`,
                                },
                                boxShadow: 'none',
                                height: 52,
                                fontSize: 14,
                                fontWeight: 400,
                                lineHeight: "20px",
                                borderRadius: 8,
                                backgroundColor: BLACK.B_10
                            }}
                            placeholder="Product Type"
                            disabled={true}
                        />
                    </div>
                    <div className="mb-6" style={{ height: 72 }} >
                        <div className="mb-1" style={{ fontSize: 14, fontWeight: 500, lineHeight: "20px", color: BLACK.B_40 }} >BCG Type</div>
                        <Select
                            value={state.bcg_type}
                            onChange={onSelectBcgTypeHandler}
                            placeholder="Select BCG Type"
                            indicator={
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M9.99984 12.4791C9.88873 12.4791 9.78456 12.4617 9.68734 12.427C9.59012 12.3923 9.49984 12.3333 9.4165 12.2499L5.58317 8.41659C5.43039 8.26381 5.354 8.06936 5.354 7.83325C5.354 7.59714 5.43039 7.4027 5.58317 7.24992C5.73595 7.09714 5.93039 7.02075 6.1665 7.02075C6.40262 7.02075 6.59706 7.09714 6.74984 7.24992L9.99984 10.4999L13.2498 7.24992C13.4026 7.09714 13.5971 7.02075 13.8332 7.02075C14.0693 7.02075 14.2637 7.09714 14.4165 7.24992C14.5693 7.4027 14.6457 7.59714 14.6457 7.83325C14.6457 8.06936 14.5693 8.26381 14.4165 8.41659L10.5832 12.2499C10.4998 12.3333 10.4096 12.3923 10.3123 12.427C10.2151 12.4617 10.1109 12.4791 9.99984 12.4791Z" fill="#1C1C1C" fill-opacity="0.4" />
                                </svg>
                            }
                            sx={{
                                height: 52,
                                borderRadius: 8,
                                [`& .${selectClasses.indicator}`]: {
                                    transition: '0.2s',
                                    [`&.${selectClasses.expanded}`]: {
                                        transform: 'rotate(-180deg)',
                                    },
                                },
                            }}
                        >
                            {
                                ["BRAND", "GENERIC", "COMPETITION"]?.map(el => {
                                    return <Option key={el} value={el}>{el}</Option>
                                })
                            }
                        </Select>
                    </div>
                </DialogContent>
                <div className="flex items-center px-6" style={{ height: 88, borderTop: `1px solid ${BLACK.B_10}` }} >
                    <Button
                        loading={loadingOnSave}
                        sx={{
                            height: 40,
                            width: "100%",
                            fontSize: 14,
                            fontWeight: 500,
                            backgroundColor: PRIMARY.P_BLue,
                            lineHeight: "20px",
                            ":hover": {
                                backgroundColor: PRIMARY.P_BLue
                            },
                            borderRadius: 8
                        }}
                        onClick={onSaveClickHandler}
                    >Save</Button>
                </div>
            </Drawer>
        </div>
    )
}

export default ProductMaster;
